import { Button, Form, Group, Modal, Text, useFormSubmissionController, useMelioForm, useToast } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';

import { useFeedbackModalSchema } from './schema';

type SubscriptionFeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const SubscriptionFeedbackModalActivity = ({ isOpen, onSuccess, onClose }: SubscriptionFeedbackModalProps) => {
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const { formatMessage } = useMelioIntl();
  const toast = useToast();
  const { submitButtonProps, onSubmissionStateChange, reset } = useFormSubmissionController();
  const isModalOpen = isOpen && isPlatformSubscriptionsEnabled;
  const { registerField, formProps, formState } = useMelioForm({
    onSubmit: () => {
      const onFeedbackPromise = new Promise((resolve) => resolve(true));
      void onFeedbackPromise
        .then(() => {
          reset?.();
          if (onSuccess) {
            onSuccess();
          } else {
            onClose();
          }
        })
        .catch(() => {
          toast({ type: 'error', title: formatMessage('activities.subscription.feedbackModal.toast.error') });
        });
    },
    schema: useFeedbackModalSchema(),
    defaultValues: {
      reason: '',
      feedback: '',
    },
    onSubmissionStateChange,
  });

  const onCloseHandler = () => {
    reset?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onCloseHandler}
      header={formatMessage('activities.subscription.feedbackModal.title')}
      data-testid="subscription-feedback-modal-activity"
    >
      <Text>{formatMessage('activities.subscription.feedbackModal.description')}</Text>
      <Form {...formProps}>
        <Form.RadioGroup
          marginTop="m"
          paddingBottom="none"
          {...registerField('reason')}
          variant="vertical"
          options={[
            {
              mainLabelProps: { label: formatMessage('activities.subscription.feedbackModal.options.usage') },
              value: 'opt1',
            },
            {
              mainLabelProps: { label: formatMessage('activities.subscription.feedbackModal.options.billed') },
              value: 'opt2',
            },
            {
              mainLabelProps: { label: formatMessage('activities.subscription.feedbackModal.options.need') },
              value: 'opt3',
            },
            {
              mainLabelProps: { label: formatMessage('activities.subscription.feedbackModal.options.alternative') },
              value: 'opt4',
            },
            {
              mainLabelProps: { label: formatMessage('activities.subscription.feedbackModal.options.expensive') },
              value: 'opt5',
            },
            {
              mainLabelProps: { label: formatMessage('activities.subscription.feedbackModal.options.other') },
              value: 'opt6',
            },
          ]}
        />
        <Form.TextArea
          {...registerField('feedback')}
          placeholder={formatMessage('activities.subscription.feedbackModal.textarea')}
          aria-label={formatMessage('activities.subscription.feedbackModal.textarea')}
        />
        <Group justifyContent="flex-end">
          <Button
            variant="tertiary"
            label={formatMessage('activities.subscription.feedbackModal.secondaryButton')}
            onClick={onCloseHandler}
            data-testid="subscription-feedback-modal-cancel"
          />
          <Button
            label={formatMessage('activities.subscription.feedbackModal.primaryButton')}
            {...submitButtonProps}
            isDisabled={!formState.isValid}
            data-testid="subscription-feedback-modal-submit"
          />
        </Group>
      </Form>
    </Modal>
  );
};
